/**
 * Exec dashboard chart system (quick-261104-ia3). One look for every tile:
 * recessive axes, thin marks, 4px rounded bar ends, plain-English tooltips.
 *
 * Palettes are validated with the dataviz validator (light surface):
 *  - SERIES: categorical slots 1–3 (blue, orange, aqua) — adjacent CVD pass.
 *  - STAGE_BANDS: 5-step single-hue ordinal ramp (blue 250/350/450/550/650) —
 *    monotone, every step gap ≥ 0.06 ΔL, light end ≥ 2:1. Seven stages fold to
 *    five bands for the stacked trend (Business+Technical Win = "Win",
 *    Confirmed+Negotiation = "Closing"); tooltips still list every stage.
 *  - POTENTIAL: categorical orange — a different hue so "not yet an opp" never
 *    reads as a pipeline stage.
 */
import type { EChartsOption } from 'echarts';

export const INK = { primary: '#0f172a', secondary: '#475569', muted: '#94a3b8', grid: '#eef2f6', axis: '#cbd5e1' };

export const SERIES = { blue: '#2a78d6', orange: '#eb6834', aqua: '#1baf7a', red: '#e34948', violet: '#6250d6' };

export const POTENTIAL_COLOR = SERIES.orange;

export const STAGE_BANDS: { key: string; label: string; stages: string[]; color: string }[] = [
  { key: 'discovery', label: 'Discovery', stages: ['Discovery'], color: '#86b6ef' },
  { key: 'demo', label: 'Demo', stages: ['Demo'], color: '#5598e7' },
  { key: 'scoping', label: 'Scoping', stages: ['Scoping'], color: '#2a78d6' },
  { key: 'win', label: 'Business / Technical Win', stages: ['Business Win', 'Technical Win'], color: '#1c5cab' },
  { key: 'closing', label: 'Confirmed / Negotiation', stages: ['Confirmed', 'Negotiation'], color: '#104281' },
];

/** Colour for a single stage (its band's colour). */
export function stageColor(stage: string): string {
  return STAGE_BANDS.find((b) => b.stages.includes(stage))?.color ?? INK.muted;
}

const FONT = 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

/** Tooltip box shared by every chart — white card, readable ink. */
export const TOOLTIP_BASE = {
  backgroundColor: '#ffffff',
  borderColor: '#e2e8f0',
  borderWidth: 1,
  padding: [8, 10] as number[],
  textStyle: { color: INK.primary, fontSize: 12, fontFamily: FONT },
  extraCssText: 'box-shadow:0 4px 16px rgba(15,23,42,.10);border-radius:8px;max-width:280px;white-space:normal;',
};

/** Base option: tight grid, recessive axes, no ECharts legend (tiles render HTML legends). */
export function baseOption(opts: { yMoney?: boolean; yPercent?: boolean; yDays?: boolean; left?: number } = {}): EChartsOption {
  const fmt = (raw: number): string => {
    const sign = raw < 0 ? '−' : '';
    const v = Math.abs(raw);
    return sign + fmtAbs(v);
  };
  const fmtAbs = (v: number): string => {
    if (opts.yMoney) return v >= 1_000_000 ? `$${String(+(v / 1_000_000).toFixed(1))}M` : v >= 1000 ? `$${String(Math.round(v / 1000))}K` : `$${String(v)}`;
    if (opts.yPercent) return `${String(Math.round(v * 100))}%`;
    if (opts.yDays) return `${String(v)}d`;
    return v >= 1000 ? `${String(+(v / 1000).toFixed(1))}k` : String(v);
  };
  return {
    animationDuration: 300,
    textStyle: { fontFamily: FONT },
    grid: { left: opts.left ?? 44, right: 10, top: 10, bottom: 24, containLabel: false },
    legend: { show: false },
    xAxis: {
      type: 'category',
      axisLine: { lineStyle: { color: INK.axis } },
      axisTick: { show: false },
      axisLabel: { color: INK.secondary, fontSize: 11, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      splitNumber: 3,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: INK.grid } },
      axisLabel: { color: INK.muted, fontSize: 10, formatter: fmt },
    },
    tooltip: { ...TOOLTIP_BASE, trigger: 'axis', axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148,163,184,.12)' } } },
  };
}

/** Tooltip HTML helpers — a bold heading, then label/value rows with a colour chip. */
export function tipHead(text: string): string {
  return `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(text)}</div>`;
}
export function tipRow(color: string | null, label: string, value: string): string {
  const chip = color
    ? `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};margin-right:6px"></span>`
    : '';
  return `<div style="display:flex;justify-content:space-between;gap:12px"><span>${chip}${escapeHtml(label)}</span><b>${escapeHtml(value)}</b></div>`;
}
export function tipNote(text: string): string {
  return `<div style="margin-top:6px;color:${INK.secondary};font-size:11px;line-height:1.35">${escapeHtml(text)}</div>`;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/** 4px rounded data-end for a bar anchored at the baseline (top segment only when stacked). */
export const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
